/**
 * Nút "Lấy toạ độ hiện tại" cho các ô GPS của Hộ và Vườn.
 *
 * Vì sao cần: trước đây điều tra viên phải đọc toạ độ từ một app khác rồi gõ lại hai số
 * bảy chữ số thập phân. Gõ sai một chữ số ở vĩ độ là dịch ~11m, đủ để rơi ra ngoài bán
 * kính 50m mà `POST /plots` dùng để phát hiện vườn trùng (US A4) — nghĩa là lỗi gõ tay
 * không bị chặn, nó chỉ tạo thêm một vườn trùng nữa.
 *
 * Theo nguyên tắc test của `apps/web/vitest.config.js` (môi trường `node`, không jsdom):
 * phần logic thuần — dựng HTML, làm tròn, diễn giải lỗi và độ chính xác — tách ra thành
 * hàm export được kiểm bằng test; chỉ `attachGpsCapture` đụng DOM và `navigator`.
 */
import { esc } from './util.js';

/**
 * `maximumAge: 0` — không nhận vị trí cache. Điều tra viên đi từ vườn này sang vườn kế
 * bên trong cùng một phiên làm việc; một toạ độ cache 10 phút trước là toạ độ vườn trước.
 */
const GEO_OPTIONS = { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 };

/** Bằng `PLOT_DUPLICATE_RADIUS_M` mặc định của máy chủ — xem `farmer-master-data` §4. */
const DUPLICATE_RADIUS_M = 50;

/** `numeric(10,7)` ở DB: giữ đúng 7 chữ số thập phân, phần dài hơn chỉ là ảo giác chính xác. */
export function formatCoord(value) {
  return Number(value).toFixed(7).replace(/0+$/, '').replace(/\.$/, '');
}

/**
 * Diễn giải `GeolocationPositionError`. Ba mã của chuẩn W3C, mỗi mã một việc phải làm
 * khác nhau — nên không gộp thành một câu "không lấy được toạ độ".
 */
export function gpsErrorMessage(err) {
  switch (err?.code) {
    case 1: // PERMISSION_DENIED
      return 'Trình duyệt đã bị từ chối quyền vị trí. Mở phần cài đặt quyền của trang (biểu tượng ổ khoá cạnh địa chỉ) và cho phép Vị trí, rồi thử lại.';
    case 2: // POSITION_UNAVAILABLE
      return 'Thiết bị chưa xác định được vị trí. Bật GPS/Định vị của máy, ra chỗ thoáng rồi thử lại.';
    case 3: // TIMEOUT
      return 'Quá 15 giây chưa có tín hiệu. Ra chỗ trống trời rồi thử lại — trong nhà hoặc dưới tán cây dày thường không bắt được.';
    default:
      return err?.message || 'Không lấy được toạ độ.';
  }
}

/**
 * Ghi chú độ chính xác. Sai số lớn hơn bán kính chống trùng thì toạ độ vẫn nên lưu (có
 * còn hơn không) nhưng phải nói rõ là nó chưa đủ mịn để phân biệt hai vườn cạnh nhau —
 * đó chính là việc mà con số này sẽ được dùng để làm.
 */
export function accuracyNote(accuracyM) {
  if (!Number.isFinite(accuracyM)) return { tone: 'ok', text: 'Đã lấy toạ độ hiện tại.' };
  const m = Math.round(accuracyM);
  if (m > DUPLICATE_RADIUS_M) {
    return {
      tone: 'warn',
      text: `Đã lấy toạ độ, nhưng sai số ±${m}m — rộng hơn bán kính ${DUPLICATE_RADIUS_M}m dùng để phát hiện vườn trùng. Nên đứng giữa vườn, chỗ trống trời và bấm lại.`,
    };
  }
  return { tone: 'ok', text: `Đã lấy toạ độ hiện tại · sai số ±${m}m.` };
}

/** Lý do không thể lấy toạ độ, biết trước khi bấm. `null` = bấm được. */
export function gpsUnavailableReason({ hasGeolocation, isSecureContext }) {
  if (!hasGeolocation) return 'Trình duyệt này không hỗ trợ định vị — nhập toạ độ bằng tay.';
  // Chrome/Safari khoá Geolocation API ngoài HTTPS. Không nói ra thì người dùng bấm và
  // chỉ nhận được PERMISSION_DENIED, một câu không dẫn tới hành động nào đúng.
  if (!isSecureContext) return 'Chỉ lấy được toạ độ khi trang chạy qua HTTPS (hoặc localhost). Nhập toạ độ bằng tay.';
  return null;
}

/**
 * Đọc cặp toạ độ từ hai ô nhập, trả `{ gpsLat, gpsLng }` để đưa vào payload hoặc
 * `{ error }` để hiện dưới form.
 *
 * Cần hàm này vì ô toạ độ là ô text (`inputmode="decimal"`, để bàn phím điện thoại ra
 * đúng bộ số và để dán được toạ độ từ app khác) chứ không phải `type="number"` — nghĩa là
 * trình duyệt không chặn gì. Không kiểm ở đây thì `Number('12,66')` ra `NaN`, `NaN` qua
 * `JSON.stringify` thành `null`, `@IsOptional()` của DTO bỏ qua `null`, và vườn được lưu
 * KHÔNG có toạ độ — im lặng, kèm việc bỏ luôn bước kiểm trùng GPS (US A4).
 *
 * Đòi đủ cả hai ô cũng vì lý do đó: một mình vĩ độ không kiểm trùng được, nó chỉ là dữ
 * liệu chết trong DB.
 */
export function readGpsPair(latRaw, lngRaw) {
  const lat = String(latRaw ?? '').trim();
  const lng = String(lngRaw ?? '').trim();
  if (!lat && !lng) return { gpsLat: undefined, gpsLng: undefined };
  if (!lat || !lng) return { error: 'Nhập đủ cả vĩ độ và kinh độ, hoặc để trống cả hai.' };

  const nLat = Number(lat);
  const nLng = Number(lng);
  if (!Number.isFinite(nLat) || nLat < -90 || nLat > 90) {
    return { error: 'Vĩ độ phải là số từ -90 đến 90 (dùng dấu chấm thập phân, ví dụ 12.6601235).' };
  }
  if (!Number.isFinite(nLng) || nLng < -180 || nLng > 180) {
    return { error: 'Kinh độ phải là số từ -180 đến 180 (dùng dấu chấm thập phân, ví dụ 108.05).' };
  }
  return { gpsLat: nLat, gpsLng: nLng };
}

/**
 * Một hàng form gồm cặp ô vĩ độ/kinh độ + nút lấy toạ độ. Tên ô cố định `gpsLat`/`gpsLng`
 * — cả `households` lẫn `plots` dùng đúng hai tên này ở DTO.
 */
export function gpsFieldHtml({ label = 'Toạ độ GPS (lat, lng)', latValue = '', lngValue = '' } = {}) {
  return `<div class="form-row" data-gps-field>
    <label>${esc(label)}</label>
    <div class="input-pair">
      <input name="gpsLat" placeholder="Vĩ độ" inputmode="decimal" value="${esc(latValue ?? '')}">
      <input name="gpsLng" placeholder="Kinh độ" inputmode="decimal" value="${esc(lngValue ?? '')}">
    </div>
    <div class="gps-capture">
      <button class="btn btn-tonal sm" type="button" data-gps-locate>
        <span class="ms ms-sm">my_location</span>Lấy toạ độ hiện tại
      </button>
      <span class="hint-text" data-gps-status aria-live="polite"></span>
    </div>
  </div>`;
}

/**
 * Nối hành vi cho mọi hàng GPS trong `scope`. Gọi sau khi HTML đã vào DOM.
 *
 * `type="button"` ở nút là bắt buộc (đã đặt trong `gpsFieldHtml`): nút mặc định trong
 * form là submit, bấm "Lấy toạ độ" sẽ gửi luôn phiếu chưa nhập xong.
 */
export function attachGpsCapture(scope) {
  scope.querySelectorAll('[data-gps-field]').forEach((field) => {
    const btn = field.querySelector('[data-gps-locate]');
    const status = field.querySelector('[data-gps-status]');
    const latInput = field.querySelector('input[name="gpsLat"]');
    const lngInput = field.querySelector('input[name="gpsLng"]');
    if (!btn || !latInput || !lngInput) return;

    const show = (tone, text) => {
      status.textContent = text;
      status.dataset.tone = tone;
    };

    const reason = gpsUnavailableReason({
      hasGeolocation: !!navigator.geolocation,
      isSecureContext: window.isSecureContext !== false,
    });
    if (reason) {
      btn.disabled = true;
      btn.title = reason;
      show('warn', reason);
      return;
    }

    btn.addEventListener('click', () => {
      // Ô đã có số thường là toạ độ chép tay của đúng vườn này — ghi đè mà không hỏi thì
      // một cú bấm nhầm xoá mất dữ liệu người dùng vừa gõ.
      const daCoSo = latInput.value.trim() !== '' || lngInput.value.trim() !== '';
      if (daCoSo && !window.confirm('Ghi đè toạ độ đang có bằng vị trí hiện tại của thiết bị?')) return;

      btn.disabled = true;
      show('pending', 'Đang xin quyền và lấy toạ độ…');
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          latInput.value = formatCoord(pos.coords.latitude);
          lngInput.value = formatCoord(pos.coords.longitude);
          // Ô được điền bằng script không tự phát `input` — form nào đang theo dõi thay
          // đổi (autosave, bật/tắt nút Lưu) sẽ không biết gì nếu thiếu hai dòng này.
          latInput.dispatchEvent(new Event('input', { bubbles: true }));
          lngInput.dispatchEvent(new Event('input', { bubbles: true }));
          const note = accuracyNote(pos.coords.accuracy);
          show(note.tone, note.text);
          btn.disabled = false;
        },
        (err) => {
          show('warn', gpsErrorMessage(err));
          btn.disabled = false;
        },
        GEO_OPTIONS,
      );
    });
  });
}

/**
 * Vị trí người gửi lúc gửi đề xuất sửa (Doc/11a E2): tự gắn, không bắt nhập. Lấy không
 * được — từ chối quyền, không có HTTPS, trong nhà — thì trả `null` và đề xuất vẫn đi,
 * người duyệt thấy "không có vị trí". Chờ ngắn hơn nút lấy toạ độ: đây không phải dữ liệu
 * của vườn, không đáng bắt điều tra viên đứng đợi 15 giây.
 */
export function currentPositionOrNull({ timeoutMs = 8000 } = {}) {
  return new Promise((resolve) => {
    if (typeof navigator === 'undefined' || !navigator.geolocation || window.isSecureContext === false) {
      resolve(null);
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude, accuracyM: pos.coords.accuracy }),
      () => resolve(null),
      { enableHighAccuracy: true, timeout: timeoutMs, maximumAge: 60_000 },
    );
  });
}
